import { ColDef } from '@ag-grid-community/core';
import {
    SelectOption,
    TransactionStatusCode,
    TransactionStatusCodeMap,
    TransactionTypeChar,
    TransactionTypeString
} from '@tassat/common-definitions';
import { UserRecord, UserRole } from '@tassat/fred-definitions';
import { TransactionHistoryTableOptions } from '@tassat/fred-extended-definitions';

import { validateNumbersOnly } from 'Lib/validationHelpers';
import { IFeaturesContext } from 'SharedComponents/hooks/useFeatures';

import { getInitiatorBalanceWithCurrency } from './columnHelpers';

export const getAdminTransactionHistoryColumnDefs = (opts: {
    features: IFeaturesContext;
    currentUser: UserRecord;
    currentRole: UserRole;
    tableOptions: TransactionHistoryTableOptions;
    getMappedColumn: (column: any) => any;
    getBalance: (params: any) => any;
    adminTransactionTypeOptions: (features: IFeaturesContext) => SelectOption[];
}): ColDef[] => {
    const {
        features,
        currentUser,
        currentRole,
        tableOptions,
        getMappedColumn,
        getBalance,
        adminTransactionTypeOptions
    } = opts;

    const columnDefs: ColDef[] = [
        {
            field: 'AuditTime',
            headerName: 'Date/Time',
            cellRenderer: 'dateCellRender',
            minWidth: 80,
            menuTabs: [],
            flex: 1
        },
        {
            field: 'type',
            headerName: 'Type',
            cellRenderer: 'transType',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'select',
                selectOptions: adminTransactionTypeOptions(features),
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            valueGetter: () => ({ isAdmin: true }),
            flex: 1
        },
        {
            field: 'SenderUsername',
            headerName: 'Username',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'senderFirm',
            headerName: 'From Firm & Account',
            cellRenderer: 'firmWallet',
            cellRendererParams: { direction: 'sender' },
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            minWidth: 100,
            flex: 1
        },
        {
            field: 'recipientFirm',
            headerName: 'To Firm & Account',
            cellRenderer: 'firmWallet',
            cellRendererParams: { direction: 'receiver' },
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'RequestId',
            headerName: 'Request ID',
            cellRenderer: 'textCell',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'textEquals',
                validators: [validateNumbersOnly],
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'Approver',
            headerName: 'Approver',
            cellRenderer: 'textCell',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'APIConnectionName',
            headerName: 'API Name',
            cellRenderer: 'multiLineText',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'Amount',
            headerName: 'Amount',
            cellRenderer: 'formatAssetsAmount',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: {
                getMappedColumn,
                getMappedTableColumn
            },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                getMappedTableColumn,
                inputType: 'assetAmount',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            valueGetter: ({ data }) => ({ ...data }),
            flex: 1
        },
        {
            field: 'SenderCurrentBalance',
            headerName: 'Initiator\'s Balance',
            cellRenderer: 'formatCurrentBalance',
            valueGetter: ({ data }) => data,
            menuTabs: [],
            flex: 1
        },
        {
            field: 'UIStatus',
            cellRenderer: 'tranStatus',
            cellRendererParams: {
                currentUser,
                currentRole
            },
            headerName: 'Status',
            headerComponent: tableOptions.SelectedFilter === 'none' ? 'externalFilterHeader' : null,
            headerComponentParams: tableOptions.SelectedFilter === 'none' ? { getMappedColumn } : {},
            filter: tableOptions.SelectedFilter === 'none' ? 'externalFilter' : '',
            filterParams: tableOptions.SelectedFilter === 'none' ? {
                getMappedColumn,
                inputType: 'select',
                selectOptions: transactionStatusOptions,
                hideSort: true
            } : {},
            menuTabs: ['filterMenuTab'],
            flex: 1
        }
    ];

    return columnDefs;
};

export const getClientTransactionHistoryColumnDefs = (opts: {
    features: IFeaturesContext;
    currentUser: UserRecord;
    currentRole: UserRole;
    tableOptions: TransactionHistoryTableOptions;
    getMappedColumn: (column: any) => any;
    getBalance: (params: any) => any;
    clientTransactionTypeOptions: (features: IFeaturesContext) => SelectOption[];
}): ColDef[] => {
    const {
        features,
        currentUser,
        currentRole,
        tableOptions,
        getMappedColumn,
        getBalance,
        clientTransactionTypeOptions
    } = opts;

    const columnDefs: ColDef[] = [
        {
            field: 'UIStatus',
            cellRenderer: 'tranStatus',
            cellRendererParams: {
                currentUser,
                currentRole
            },
            headerName: 'Status',
            headerComponent: tableOptions.SelectedFilter === 'none' ? 'externalFilterHeader' : null,
            headerComponentParams: tableOptions.SelectedFilter === 'none' ? { getMappedColumn } : {},
            filter: tableOptions.SelectedFilter === 'none' ? 'externalFilter' : '',
            filterParams: tableOptions.SelectedFilter === 'none' ? {
                inputType: 'select',
                getMappedColumn,
                selectOptions: transactionStatusOptions,
                hideSort: true
            } : {},
            menuTabs: ['filterMenuTab'],
            flex: 3
        },
        {
            field: 'AuditTime',
            headerName: 'Date/Time',
            cellRenderer: 'dateCellRender',
            minWidth: 130,
            menuTabs: [],
            flex: 4
        },
        {
            field: 'type',
            headerName: 'Type',
            cellRenderer: 'transType',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'select',
                selectOptions: clientTransactionTypeOptions(features),
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            valueGetter: () => ({ isAdmin: false }),
            flex: 3
        },
        {
            field: 'SenderUsername',
            headerName: 'Username',
            cellRenderer: 'userIdCell',
            headerComponent: 'externalFilterHeader',
            cellRendererParams: { features },
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 4
        },
        {
            field: 'RecipientAccountName',
            headerName: 'Counterparty',
            cellRenderer: 'counterpartyWallet',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'clientCounterparty',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 4
        },
        {
            field: 'RequestId',
            headerName: 'Request ID',
            cellRenderer: 'textCell',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'textEquals',
                validators: [validateNumbersOnly],
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 4
        },
        {
            field: 'ConfirmationId',
            headerName: 'Confirmation ID',
            cellRenderer: 'textCell',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 4
        },
        {
            field: 'Approver',
            headerName: 'Approver',
            cellRenderer: 'textCell',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 4
        },
        {
            field: 'APIConnectionName',
            headerName: 'API Name',
            cellRenderer: 'multiLineText',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 4
        },
        {
            field: 'Amount',
            headerName: 'Amount',
            cellRenderer: 'formatAssetsAmount',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: {
                getMappedColumn,
                getMappedTableColumn
            },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                getMappedTableColumn,
                inputType: 'assetAmount',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            valueGetter: ({ data }) => ({ ...data }),
            flex: 4
        },
        {
            field: 'SenderCurrentBalance',
            headerName: 'Balance',
            cellRenderer: 'formatCurrentBalance',
            valueGetter: getInitiatorBalanceWithCurrency,
            menuTabs: [],
            flex: 4
        }
    ];

    return columnDefs;
};

export const getMappedColumn = (column) => {
    switch (column) {
        case 'UIStatus':
            return 'Status';
        case 'TransType':
        case 'type':
            return 'tranType';
        case 'senderFirm':
            return 'senderFirm';
        case 'recipientFirm':
            return 'recipientFirm';
        case 'RecipientBank':
            return 'recipientBank';
        case 'SenderBank':
            return 'senderBank';
        case 'SenderAvailableBalance':
            return 'SenderAvailBalance';
        case 'SenderWalletShortName':
            return 'senderWalletShortName';
        case 'RecipientWalletShortName':
            return 'recipientWalletShortName';
        case 'Username':
            return 'username';
        case 'APIConnectionName':
            return 'apiConnName';
        case 'AuditDateTime':
            return 'auditTime';
        default:
            return column;
    }
};

export const getMappedTableColumn = (column) => {
    switch (column) {
        case 'fiatAmount':
            return 'Amount';
        case 'assetQuantity':
            return 'Amount';
        default:
            return column;
    }
};

export const transactionStatusOptions: SelectOption[] = [
    {
        label: 'Select an option...',
        value: ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.CANCELED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.CANCELED]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.DECLINED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.DECLINED]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.COMPLETED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.COMPLETED]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.FAILED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.FAILED]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.NEW]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.NEW]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.PENDING]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.PENDING]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.PROCESSING]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.PROCESSING]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.REJECTED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.REJECTED]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.ACKNOWLEDGED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.ACKNOWLEDGED]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.EXPIRED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.EXPIRED]?.displayValue ?? ''
    }
];

export const adminTransactionTypeOptions: (features: IFeaturesContext) => SelectOption[] = (features) => {
    const transactionTypes = [
        {
            label: 'Select an option...',
            value: ''
        },
        {
            label: TransactionTypeString.deposit,
            value: TransactionTypeChar.deposit.toUpperCase()
        },
        {
            label: TransactionTypeString.transfer,
            value: TransactionTypeChar.send.toUpperCase()
        },
        {
            label: TransactionTypeString.withdrawal,
            value: TransactionTypeChar.withdrawal.toUpperCase()
        },
        {
            label: TransactionTypeString.int_distribution,
            value: TransactionTypeChar.int_distribution.toUpperCase()
        },
        {
            label: TransactionTypeString.order,
            value: TransactionTypeChar.order.toUpperCase()
        }
    ];

    return transactionTypes;
};

export const clientTransactionTypeOptions: (features: IFeaturesContext) => SelectOption[] = (features) => {
    const transactionTypes = [
        {
            label: 'Select an option...',
            value: ''
        },
        {
            label: TransactionTypeString.deposit,
            value: TransactionTypeChar.deposit.toUpperCase()
        },
        {
            label: TransactionTypeString.send,
            value: TransactionTypeChar.send
        },
        {
            label: TransactionTypeString.receive,
            value: TransactionTypeChar.receive
        },
        {
            label: TransactionTypeString.withdrawal,
            value: TransactionTypeChar.withdrawal.toUpperCase()
        },
        {
            label: TransactionTypeString.int_distribution,
            value: TransactionTypeChar.int_distribution.toUpperCase()
        },
        {
            label: TransactionTypeString.order,
            value: TransactionTypeChar.order.toUpperCase()
        }
    ];

    return transactionTypes;
};